import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Spinner, Modal, Button } from 'react-bootstrap';
import { ApiGet, ApiPost } from '../../../../ApiServices/ApiServices';
import { toast } from 'react-toastify';

const POS = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', email: '', orderType: 'dine-in', address: '' });
  const [processingOrder, setProcessingOrder] = useState(false);

  // Customer search states
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerList, setCustomerList] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Addon states
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [selectedItemForAddon, setSelectedItemForAddon] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [itemAddons, setItemAddons] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(false);

  // Mobile cart state
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  // Customer address states
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);

  const primaryColor = '#d74440';

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
      if (window.innerWidth >= 992) {
        setShowMobileCart(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchMenuItems();
  }, [selectedCategory, selectedSubcategory, searchTerm]);

  useEffect(() => {
    if (selectedCategory !== 'all') {
      fetchSubcategories(selectedCategory);
    } else {
      setSubcategories([]);
    }
  }, [selectedCategory]);

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const categoryId = selectedCategory !== 'all' ? selectedCategory : '';
      const subcategoryId = selectedSubcategory !== 'all' ? selectedSubcategory : '';
      const result = await ApiGet(`/api/cashier/menu_items/filter?categoryId=${categoryId}&subcategoryId=${subcategoryId}&searchValue=${searchTerm}`);
      if (result.success) {
        setMenuItems(result.success.data.data.records || []);
      }
    } catch (err) {
      toast.error('Failed to fetch menu items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const result = await ApiGet('/api/cashier/menu_category/all');
      if (result.success) {
        setCategories(result.success.data?.data || result.success.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const result = await ApiGet(`/api/cashier/menu_subcategory/categoryId?categoryId=${categoryId}`);
      if (result.success) {
        setSubcategories(result.success.data?.data || result.success.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch subcategories');
    }
  };

  // Search customers by name
  const searchCustomers = async (searchValue) => {
    if (!searchValue.trim()) {
      setCustomerList([]);
      setShowCustomerDropdown(false);
      return;
    }
    setCustomerLoading(true);
    try {
      const result = await ApiGet('/api/cashier/customers/search', {
        searchValue: searchValue,
        pageNumber: 0,
        pageSize: 10
      });
      if (result.success) {
        const data = result.success.data?.data || result.success.data || {};
        setCustomerList(data.records || []);
        setShowCustomerDropdown(true);
      }
    } catch (err) {
      console.error('Failed to search customers');
      setCustomerList([]);
    } finally {
      setCustomerLoading(false);
    }
  };

  // Handle customer search input change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (customerSearch && !selectedCustomer) {
        searchCustomers(customerSearch);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearch, selectedCustomer]);

  // Fetch customer delivery addresses
  const fetchCustomerAddresses = async (customerId) => {
    if (!customerId) {
      setCustomerAddresses([]);
      return;
    }
    setAddressesLoading(true);
    try {
      const result = await ApiGet(`/api/cashier/customer_delivery_addresses/customerId?customerId=${customerId}`);
      if (result.success) {
        const addresses = result.success.data?.data || result.success.data || [];
        setCustomerAddresses(Array.isArray(addresses) ? addresses : []);
      } else {
        setCustomerAddresses([]);
      }
    } catch (err) {
      console.error('Failed to fetch customer addresses:', err);
      setCustomerAddresses([]);
    } finally {
      setAddressesLoading(false);
    }
  };

  // Select customer from dropdown
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerInfo({
      ...customerInfo,
      name: customer.name || '',
      phone: customer.mobileNumber || '',
      email: customer.email || ''
    });
    setCustomerSearch(customer.name || '');
    setShowCustomerDropdown(false);
    // Fetch addresses for this customer
    fetchCustomerAddresses(customer.id);
    // Reset selected address
    setSelectedAddress(null);
  };

  // Clear selected customer
  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerInfo({
      ...customerInfo,
      name: '',
      phone: '',
      email: '',
      address: ''
    });
    setCustomerList([]);
    // Clear addresses
    setCustomerAddresses([]);
    setSelectedAddress(null);
    setShowAddressDropdown(false);
  };

  // Build full address string from address object
  const buildFullAddress = (addr) => {
    if (!addr) return '';
    const parts = [
      addr.addressLine1,
      addr.addressLine2,
      addr.landmark ? `Near ${addr.landmark}` : null
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Select address from dropdown
  const handleSelectAddress = (address) => {
    setSelectedAddress(address);
    setCustomerInfo({
      ...customerInfo,
      address: buildFullAddress(address)
    });
    setShowAddressDropdown(false);
  };

  // Fetch addon items from API for a given addonId
  const fetchAddonItems = async (addonId) => {
    if (!addonId) {
      setItemAddons([]);
      return;
    }
    setAddonsLoading(true);
    try {
      const result = await ApiGet(`/api/cashier/addons_items/addonId?addonId=${addonId}`);
      if (result.success) {
        const addons = result.success.data?.data || result.success.data || [];
        setItemAddons(Array.isArray(addons) ? addons : []);
      } else {
        setItemAddons([]);
      }
    } catch (err) {
      console.error('Failed to fetch addon items:', err);
      setItemAddons([]);
    } finally {
      setAddonsLoading(false);
    }
  };

  // Open addon modal for an item
  const openAddonModal = async (item, e) => {
    e.stopPropagation();
    setSelectedItemForAddon(item);
    setSelectedAddons([]);
    setItemAddons([]);
    setShowAddonModal(true);

    // Fetch addon items if item has addonsId
    if (item?.addonsId?.id) {
      await fetchAddonItems(item.addonsId.id);
    }
  };

  // Toggle addon selection
  const toggleAddon = (addon) => {
    const exists = selectedAddons.find(a => a.id === addon.id);
    if (exists) {
      setSelectedAddons(selectedAddons.filter(a => a.id !== addon.id));
    } else {
      // Add addon with default quantity 1
      setSelectedAddons([...selectedAddons, { ...addon, quantity: 1 }]);
    }
  };

  // Update addon quantity
  const updateAddonQuantity = (addonId, delta, e) => {
    e.stopPropagation();
    setSelectedAddons(selectedAddons.map(addon => {
      if (addon.id === addonId) {
        const newQty = Math.max(1, addon.quantity + delta);
        return { ...addon, quantity: newQty };
      }
      return addon;
    }));
  };

  // Add item with addons to cart
  const addItemWithAddons = () => {
    if (!selectedItemForAddon) return;

    // Calculate addons total with quantity
    const addonsTotal = selectedAddons.reduce((sum, addon) => sum + ((addon.price || 0) * (addon.quantity || 1)), 0);
    const cartItemId = `${selectedItemForAddon.id}-${Date.now()}`; // Unique ID for items with different addons

    const cartItem = {
      ...selectedItemForAddon,
      cartItemId,
      addons: selectedAddons,
      addonsTotal,
      totalPrice: selectedItemForAddon.price + addonsTotal,
      quantity: 1
    };

    setCart([...cart, cartItem]);
    toast.success(`${selectedItemForAddon.name} with addons added!`, { autoClose: 1000 });
    setShowAddonModal(false);
    setSelectedItemForAddon(null);
    setSelectedAddons([]);
  };

  const addToCart = (item) => {
    const existingItem = cart.find(c => c.id === item.id);
    if (existingItem) {
      setCart(cart.map(c =>
        c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c
      ));
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
    toast.success(`${item.name} added!`, { autoClose: 1000 });
  };

  const updateQuantity = (cartItemId, delta) => {
    setCart(cart.map(item => {
      const itemKey = item.cartItemId || item.id;
      if (itemKey === cartItemId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (cartItemId) => {
    setCart(cart.filter(item => (item.cartItemId || item.id) !== cartItemId));
  };

  const clearCart = () => {
    setCart([]);
    setCustomerInfo({ name: '', phone: '', email: '', orderType: 'dine-in', address: '' });
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerList([]);
    // Clear address states
    setCustomerAddresses([]);
    setSelectedAddress(null);
    setShowAddressDropdown(false);
  };

  const subtotal = cart.reduce((sum, item) => sum + ((item.totalPrice || item.price) * item.quantity), 0);
  const tax = subtotal * 0.05;
  const total = subtotal + tax;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.warning('Cart is empty');
      return;
    }
    setShowCheckoutModal(true);
  };

  const processOrder = async () => {
    setProcessingOrder(true);
    try {
      // Map order type to API format
      const orderTypeMap = {
        'dine-in': 'DINE_IN',
        'takeaway': 'TAKEAWAY',
        'delivery': 'DELIVERY'
      };

      const orderData = {
        cutomerId: selectedCustomer ? { id: selectedCustomer.id } : null,
        custAddressId: selectedAddress ? { id: selectedAddress.id } : null,
        orderType: orderTypeMap[customerInfo.orderType] || 'DINE_IN',
        items: cart.map(item => ({
          menu_item_id: String(item.id),
          quantity: item.quantity,
          price: item.price,
          special_instructions: '',
          addonItems: item.addons && item.addons.length > 0
            ? item.addons.map(addon => ({
                addonItemId: addon.id,
                quantity: String(addon.quantity || 1)
              }))
            : []
        }))
      };

      const result = await ApiPost('/api/cashier/orders/adds', orderData);
      if (result.success) {
        toast.success('Order placed successfully!');
        clearCart();
        setShowCheckoutModal(false);
      } else {
        toast.error(result.fail || 'Failed to place order');
      }
    } catch (err) {
      toast.error('Failed to process order');
    } finally {
      setProcessingOrder(false);
    }
  };

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div style={{ padding: isMobile ? '10px' : '15px', paddingBottom: isMobile ? '80px' : '15px', background: '#f8f9fa', minHeight: '100vh' }}>
      <Row className="g-3">
        {/* Left - Menu Items */}
        <Col lg={8} xs={12}>
          <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            {/* Header */}
            <div style={{ padding: isMobile ? '12px 15px' : '15px 20px', borderBottom: '1px solid #eee' }}>
              <h5 style={{ margin: 0, color: primaryColor, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', fontSize: isMobile ? '16px' : '18px' }}>
                <i className="bi bi-grid-3x3-gap-fill"></i>
                Point of Sale
              </h5>
            </div>

            {/* Filters */}
            <div style={{ padding: isMobile ? '10px 15px' : '15px 20px', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '0 0 auto', minWidth: isMobile ? 'auto' : '200px' }}>
                  <i className="bi bi-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999', zIndex: 1 }}></i>
                  <Form.Control
                    type="search"
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="sm"
                    style={{ paddingLeft: '35px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}
                  />
                </div>
                <Form.Select
                  size="sm"
                  value={selectedCategory}
                  onChange={(e) => { setSelectedCategory(e.target.value); setSelectedSubcategory('all'); }}
                  style={{ width: 'auto', flex: isMobile ? '1 1 calc(50% - 4px)' : '0 0 auto', minWidth: isMobile ? 'auto' : '150px', borderRadius: '6px', fontSize: '13px' }}
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </Form.Select>
                <Form.Select
                  size="sm"
                  value={selectedSubcategory}
                  onChange={(e) => setSelectedSubcategory(e.target.value)}
                  disabled={selectedCategory === 'all'}
                  style={{ width: 'auto', flex: isMobile ? '1 1 calc(50% - 4px)' : '0 0 auto', minWidth: isMobile ? 'auto' : '150px', borderRadius: '6px', fontSize: '13px', opacity: selectedCategory === 'all' ? 0.5 : 1 }}
                >
                  <option value="all">{selectedCategory === 'all' ? 'Select Category First' : 'All Subcategories'}</option>
                  {subcategories.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))}
                </Form.Select>
                <button
                  onClick={() => { setSelectedCategory('all'); setSelectedSubcategory('all'); setSearchTerm(''); }}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    background: '#fff',
                    color: '#666',
                    fontWeight: '500',
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  <i className="bi bi-x-circle"></i>
                  Clear
                </button>
              </div>
            </div>

            {/* Items Grid */}
            <div style={{ padding: isMobile ? '10px' : '20px', maxHeight: isMobile ? 'calc(100vh - 260px)' : 'calc(100vh - 220px)', overflowY: 'auto' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Spinner animation="border" style={{ color: primaryColor }} />
                </div>
              ) : menuItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                  <i className="bi bi-inbox" style={{ fontSize: '3rem', display: 'block', marginBottom: '10px' }}></i>
                  <p>No items found</p>
                </div>
              ) : (
                <Row className="g-3">
                  {menuItems.map(item => (
                    <Col key={item.id} xs={6} sm={4} md={3} lg={3}>
                      <div
                        onClick={() => addToCart(item)}
                        style={{
                          background: '#fff',
                          borderRadius: isMobile ? '8px' : '10px',
                          border: '1px solid #eee',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          if (!isMobile) {
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(215,68,64,0.15)';
                            e.currentTarget.style.borderColor = primaryColor;
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.borderColor = '#eee';
                        }}
                      >
                        <div style={{ height: isMobile ? '80px' : '100px', background: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                          {/* Veg/Non-veg */}
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            width: '16px',
                            height: '16px',
                            border: `2px solid ${item.dietaryType ? '#28a745' : '#dc3545'}`,
                            borderRadius: '3px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff'
                          }}>
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: item.dietaryType ? '#28a745' : '#dc3545'
                            }}></div>
                          </div>
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.name} style={{ maxHeight: '90px', maxWidth: '90%', objectFit: 'contain' }} />
                          ) : (
                            <i className="bi bi-image" style={{ fontSize: '2rem', color: '#ddd' }}></i>
                          )}
                        </div>
                        <div style={{ padding: isMobile ? '8px' : '10px', textAlign: 'center' }}>
                          <div style={{ fontSize: isMobile ? '11px' : '13px', fontWeight: '500', color: '#333', marginBottom: isMobile ? '3px' : '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.name}>
                            {item.name}
                          </div>
                          <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '700', color: primaryColor, marginBottom: isMobile ? '6px' : '8px' }}>
                            {formatCurrency(item.price)}
                          </div>
                          <button
                            onClick={(e) => openAddonModal(item, e)}
                            style={{
                              padding: isMobile ? '3px 8px' : '4px 12px',
                              fontSize: isMobile ? '10px' : '11px',
                              fontWeight: '500',
                              background: '#fff',
                              border: `1px solid ${primaryColor}`,
                              color: primaryColor,
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            <i className="bi bi-plus-circle me-1"></i>
                            Addons
                          </button>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          </div>
        </Col>

        {/* Right - Cart (Desktop Only) */}
        {!isMobile && (
          <Col lg={4}>
            <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', position: 'sticky', top: '15px' }}>
              {/* Cart Header */}
              <div style={{ padding: '15px 20px', background: primaryColor, borderRadius: '10px 10px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h6 style={{ margin: 0, color: '#ffffff', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="bi bi-cart3" style={{ color: '#ffffff' }}></i>
                  <span style={{ color: '#ffffff' }}>Current Order</span>
                </h6>
                <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                  {totalItems} Items
                </span>
              </div>

              {/* Cart Items */}
              <div style={{ padding: '15px', maxHeight: '300px', overflowY: 'auto', minHeight: '150px' }}>
                {cart.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px 10px', color: '#bbb' }}>
                    <i className="bi bi-cart-x" style={{ fontSize: '2.5rem', display: 'block', marginBottom: '10px' }}></i>
                    <p style={{ margin: 0, fontSize: '14px' }}>Cart is empty</p>
                  </div>
                ) : (
                  cart.map(item => {
                    const itemKey = item.cartItemId || item.id;
                    const itemPrice = item.totalPrice || item.price;
                    return (
                      <div key={itemKey} style={{ padding: '10px', background: '#f9f9f9', borderRadius: '8px', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {/* Item Image */}
                          <div style={{ width: '45px', height: '45px', borderRadius: '6px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid #eee' }}>
                            {item.imageUrl ? (
                              <img src={item.imageUrl} alt={item.name} style={{ maxWidth: '38px', maxHeight: '38px', objectFit: 'contain' }} />
                            ) : (
                              <i className="bi bi-image" style={{ color: '#ccc', fontSize: '16px' }}></i>
                            )}
                          </div>
                          {/* Item Details */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                            <div style={{ fontSize: '12px', color: '#888' }}>{formatCurrency(itemPrice)} x {item.quantity}</div>
                          </div>
                          {/* Quantity Controls */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => updateQuantity(itemKey, -1)} style={{ width: '26px', height: '26px', border: '1px solid #ddd', background: '#fff', borderRadius: '5px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>−</button>
                            <span style={{ fontWeight: '600', minWidth: '18px', textAlign: 'center', fontSize: '13px' }}>{item.quantity}</span>
                            <button onClick={() => updateQuantity(itemKey, 1)} style={{ width: '26px', height: '26px', border: '1px solid #ddd', background: '#fff', borderRadius: '5px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>+</button>
                            <button onClick={() => removeFromCart(itemKey)} style={{ width: '26px', height: '26px', border: '1px solid #fdd', background: '#fff', borderRadius: '5px', cursor: 'pointer', color: '#dc3545' }}>
                              <i className="bi bi-trash" style={{ fontSize: '11px' }}></i>
                            </button>
                          </div>
                        </div>
                        {/* Addons */}
                        {item.addons && item.addons.length > 0 && (
                          <div style={{ marginTop: '6px', paddingLeft: '55px' }}>
                            <div style={{ fontSize: '11px', color: '#666' }}>
                              <span style={{ fontWeight: '500' }}>Addons:</span> {item.addons.map(a => `${a.name} (+${formatCurrency(a.price)})`).join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Cart Footer */}
              <div style={{ padding: '15px 20px', borderTop: '1px solid #eee', background: '#fafafa', borderRadius: '0 0 10px 10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: '#666' }}>
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '13px', color: '#666' }}>
                  <span>Tax (5%)</span>
                  <span>{formatCurrency(tax)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px dashed #ddd', marginBottom: '15px' }}>
                  <span style={{ fontSize: '16px', fontWeight: '700', color: '#333' }}>Total</span>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: primaryColor }}>{formatCurrency(total)}</span>
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={cart.length === 0}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: cart.length === 0 ? '#ccc' : primaryColor,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
                    marginBottom: '8px'
                  }}
                >
                  <i className="bi bi-credit-card me-2"></i>
                  Proceed to Checkout
                </button>
                <button
                  onClick={clearCart}
                  disabled={cart.length === 0}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#fff',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontWeight: '500',
                    fontSize: '13px',
                    cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: cart.length === 0 ? 0.5 : 1
                  }}
                >
                  <i className="bi bi-x-circle me-2"></i>
                  Clear Cart
                </button>
              </div>
            </div>
          </Col>
        )}
      </Row>

      {/* Mobile Floating Cart Button */}
      {isMobile && (
        <div
          onClick={() => setShowMobileCart(true)}
          style={{
            position: 'fixed',
            bottom: '0',
            left: '0',
            right: '0',
            background: primaryColor,
            color: '#fff',
            padding: '12px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
            zIndex: 100,
            cursor: 'pointer'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i className="bi bi-cart3" style={{ fontSize: '18px' }}></i>
            <span style={{ fontWeight: '600' }}>Current Order</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
              {totalItems} Items
            </span>
            <span style={{ fontWeight: '700', fontSize: '16px' }}>{formatCurrency(total)}</span>
            <i className="bi bi-chevron-up"></i>
          </div>
        </div>
      )}

      {/* Mobile Cart Modal */}
      <Modal
        show={showMobileCart}
        onHide={() => setShowMobileCart(false)}
        centered
        fullscreen="sm-down"
        style={{ zIndex: 1055 }}
      >
        <Modal.Header style={{ background: primaryColor, borderBottom: 'none' }}>
          <Modal.Title style={{ color: '#fff', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="bi bi-cart3"></i>
            Current Order
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', marginLeft: '10px' }}>
              {totalItems} Items
            </span>
          </Modal.Title>
          <button
            onClick={() => setShowMobileCart(false)}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer' }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: '0', maxHeight: '50vh', overflowY: 'auto' }}>
          {cart.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#bbb' }}>
              <i className="bi bi-cart-x" style={{ fontSize: '3rem', display: 'block', marginBottom: '15px' }}></i>
              <p style={{ margin: 0, fontSize: '15px' }}>Your cart is empty</p>
              <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#999' }}>Add items to get started</p>
            </div>
          ) : (
            <div style={{ padding: '10px' }}>
              {cart.map(item => {
                const itemKey = item.cartItemId || item.id;
                const itemPrice = item.totalPrice || item.price;
                return (
                  <div key={itemKey} style={{ padding: '12px', background: '#f9f9f9', borderRadius: '10px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '50px', height: '50px', borderRadius: '8px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid #eee' }}>
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.name} style={{ maxWidth: '42px', maxHeight: '42px', objectFit: 'contain' }} />
                        ) : (
                          <i className="bi bi-image" style={{ color: '#ccc', fontSize: '18px' }}></i>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '4px' }}>{item.name}</div>
                        <div style={{ fontSize: '13px', color: primaryColor, fontWeight: '600' }}>{formatCurrency(itemPrice)}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button onClick={() => updateQuantity(itemKey, -1)} style={{ width: '30px', height: '30px', border: '1px solid #ddd', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontWeight: '700', fontSize: '16px' }}>−</button>
                        <span style={{ fontWeight: '700', minWidth: '24px', textAlign: 'center', fontSize: '14px' }}>{item.quantity}</span>
                        <button onClick={() => updateQuantity(itemKey, 1)} style={{ width: '30px', height: '30px', border: '1px solid #ddd', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontWeight: '700', fontSize: '16px' }}>+</button>
                        <button onClick={() => removeFromCart(itemKey)} style={{ width: '30px', height: '30px', border: '1px solid #fdd', background: '#fff', borderRadius: '6px', cursor: 'pointer', color: '#dc3545', marginLeft: '4px' }}>
                          <i className="bi bi-trash" style={{ fontSize: '13px' }}></i>
                        </button>
                      </div>
                    </div>
                    {item.addons && item.addons.length > 0 && (
                      <div style={{ marginTop: '8px', paddingLeft: '62px', fontSize: '12px', color: '#666' }}>
                        <span style={{ fontWeight: '500' }}>Addons:</span> {item.addons.map(a => a.name).join(', ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ flexDirection: 'column', padding: '15px', borderTop: '1px solid #eee', background: '#fafafa' }}>
          <div style={{ width: '100%', marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', color: '#666' }}>
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '13px', color: '#666' }}>
              <span>Tax (5%)</span>
              <span>{formatCurrency(tax)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '10px', borderTop: '1px dashed #ddd' }}>
              <span style={{ fontSize: '17px', fontWeight: '700', color: '#333' }}>Total</span>
              <span style={{ fontSize: '20px', fontWeight: '700', color: primaryColor }}>{formatCurrency(total)}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
            <button
              onClick={() => { clearCart(); setShowMobileCart(false); }}
              disabled={cart.length === 0}
              style={{
                flex: '1',
                padding: '12px',
                background: '#fff',
                color: '#666',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px',
                cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
                opacity: cart.length === 0 ? 0.5 : 1
              }}
            >
              Clear
            </button>
            <button
              onClick={() => { setShowMobileCart(false); handleCheckout(); }}
              disabled={cart.length === 0}
              style={{
                flex: '2',
                padding: '12px',
                background: cart.length === 0 ? '#ccc' : primaryColor,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '14px',
                cursor: cart.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <i className="bi bi-credit-card me-2"></i>
              Checkout
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Checkout Modal */}
      <Modal show={showCheckoutModal} onHide={() => setShowCheckoutModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #eee' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
            <i className="bi bi-receipt me-2" style={{ color: primaryColor }}></i>
            Complete Order
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px' }}>
          {/* Customer Search */}
          <Form.Group className="mb-3" style={{ position: 'relative' }}>
            <Form.Label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>Search Customer<span style={{ color: '#d74440' }}>*</span></Form.Label>
            <div style={{ position: 'relative' }}>
              <Form.Control
                type="text"
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  if (selectedCustomer) {
                    clearSelectedCustomer();
                  }
                }}
                placeholder="Search customer by name..."
                style={{ borderRadius: '8px', fontSize: '14px', paddingRight: selectedCustomer ? '40px' : '35px' }}
                disabled={selectedCustomer !== null}
              />
              {customerLoading && (
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '20px',
                  height: '20px'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #f3f3f3',
                    borderTop: `2px solid ${primaryColor}`,
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              )}
              {selectedCustomer && (
                <button
                  type="button"
                  onClick={clearSelectedCustomer}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: '#fee2e2',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="bi bi-x" style={{ color: '#dc3545', fontSize: '14px' }}></i>
                </button>
              )}
            </div>
            {/* Customer Dropdown */}
            {showCustomerDropdown && customerList.length > 0 && !selectedCustomer && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto',
                marginTop: '4px'
              }}>
                {customerList.map(customer => (
                  <div
                    key={customer.id}
                    onClick={() => handleSelectCustomer(customer)}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                  >
                    <div style={{ fontWeight: '500', color: '#333', fontSize: '13px' }}>{customer.name || 'N/A'}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{customer.mobileNumber || 'No phone'}</div>
                  </div>
                ))}
              </div>
            )}
            {showCustomerDropdown && customerList.length === 0 && customerSearch && !customerLoading && !selectedCustomer && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                textAlign: 'center',
                color: '#999',
                fontSize: '13px',
                zIndex: 1000,
                marginTop: '4px'
              }}>
                No customers found
              </div>
            )}
          </Form.Group>

          {/* Order Type */}
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>Order Type</Form.Label>
                <Form.Select
                  value={customerInfo.orderType}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, orderType: e.target.value, address: '' })}
                  style={{ borderRadius: '8px', fontSize: '14px' }}
                >
                  <option value="dine-in">Dine In</option>
                  <option value="takeaway">Takeaway</option>
                  <option value="delivery">Delivery</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          {customerInfo.orderType === 'delivery' && (
            <Form.Group className="mb-3" style={{ position: 'relative' }}>
              <Form.Label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>Delivery Address <span style={{ color: '#999', fontWeight: '400' }}>(Optional)</span></Form.Label>

              {/* Show address dropdown when customer is selected */}
              {selectedCustomer ? (
                <>
                  <div
                    onClick={() => !addressesLoading && setShowAddressDropdown(!showAddressDropdown)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      background: selectedAddress ? '#f8f9fa' : '#fff',
                      cursor: addressesLoading ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      minHeight: '42px'
                    }}
                  >
                    {addressesLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888' }}>
                        <Spinner animation="border" size="sm" style={{ color: primaryColor }} />
                        <span style={{ fontSize: '14px' }}>Loading addresses...</span>
                      </div>
                    ) : selectedAddress ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="bi bi-check-circle-fill" style={{ color: '#28a745', fontSize: '16px' }}></i>
                        <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                          {selectedAddress.addressType || 'Address'} Selected
                        </span>
                      </div>
                    ) : (
                      <span style={{ fontSize: '14px', color: '#999' }}>
                        {customerAddresses.length > 0 ? 'Select delivery address...' : 'No saved addresses'}
                      </span>
                    )}
                    {!addressesLoading && (
                      <i className={`bi bi-chevron-${showAddressDropdown ? 'up' : 'down'}`} style={{ color: '#666' }}></i>
                    )}
                  </div>

                  {/* Address Dropdown */}
                  {showAddressDropdown && customerAddresses.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      marginTop: '4px'
                    }}>
                      {customerAddresses.map((addr, index) => (
                        <div
                          key={addr.id || index}
                          onClick={() => handleSelectAddress(addr)}
                          style={{
                            padding: '12px',
                            cursor: 'pointer',
                            borderBottom: index < customerAddresses.length - 1 ? '1px solid #f0f0f0' : 'none',
                            background: selectedAddress?.id === addr.id ? '#f8f9fa' : '#fff',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = selectedAddress?.id === addr.id ? '#f8f9fa' : '#fff'}
                        >
                          <div style={{ fontWeight: '500', color: '#333', fontSize: '13px', marginBottom: '4px' }}>
                            <i className="bi bi-geo-alt me-2" style={{ color: primaryColor }}></i>
                            {addr.addressType || `Address ${index + 1}`}
                            {addr.isDefault && <span style={{ marginLeft: '8px', fontSize: '10px', background: '#e8f5e9', color: '#2e7d32', padding: '2px 6px', borderRadius: '4px' }}>Default</span>}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', paddingLeft: '22px' }}>
                            {buildFullAddress(addr) || 'No address details'}
                          </div>
                          {addr.deliveryInstructions && (
                            <div style={{ fontSize: '11px', color: '#888', paddingLeft: '22px', marginTop: '4px', fontStyle: 'italic' }}>
                              <i className="bi bi-info-circle me-1"></i>
                              {addr.deliveryInstructions}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show selected address (read-only) */}
                  {selectedAddress && (
                    <div style={{
                      marginTop: '10px',
                      padding: '12px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #eee'
                    }}>
                      <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>
                          <i className="bi bi-geo-alt-fill me-1" style={{ color: primaryColor }}></i>
                          {selectedAddress.addressType || 'Delivery Address'}
                        </span>
                        {selectedAddress.isDefault && (
                          <span style={{ fontSize: '10px', background: '#e8f5e9', color: '#2e7d32', padding: '2px 6px', borderRadius: '4px' }}>Default</span>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: '#333' }}>
                        {buildFullAddress(selectedAddress)}
                      </div>
                      {selectedAddress.deliveryInstructions && (
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '6px', fontStyle: 'italic' }}>
                          <i className="bi bi-info-circle me-1"></i>
                          {selectedAddress.deliveryInstructions}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* Show textarea for manual entry when no customer selected */
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={customerInfo.address}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                  placeholder="Enter full delivery address"
                  style={{ borderRadius: '8px', fontSize: '14px' }}
                />
              )}
            </Form.Group>
          )}
          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '15px', marginTop: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '500', color: '#555' }}>Total Amount</span>
              <span style={{ fontSize: '22px', fontWeight: '700', color: primaryColor }}>{formatCurrency(total)}</span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #eee', padding: '15px 20px' }}>
          <Button variant="light" onClick={() => setShowCheckoutModal(false)} style={{ borderRadius: '8px', fontWeight: '500' }}>
            Cancel
          </Button>
          <Button
            onClick={processOrder}
            disabled={processingOrder}
            style={{ background: primaryColor, border: 'none', borderRadius: '8px', fontWeight: '500', padding: '8px 20px' }}
          >
            {processingOrder ? <><Spinner animation="border" size="sm" className="me-2" />Processing...</> : <><i className="bi bi-check-circle me-2"></i>Place Order</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Addon Modal */}
      <Modal show={showAddonModal} onHide={() => setShowAddonModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #eee' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
            <i className="bi bi-plus-circle me-2" style={{ color: primaryColor }}></i>
            Select Addons
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px' }}>
          {selectedItemForAddon && (
            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '50px', height: '50px', borderRadius: '6px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #eee' }}>
                {selectedItemForAddon.imageUrl ? (
                  <img src={selectedItemForAddon.imageUrl} alt={selectedItemForAddon.name} style={{ maxWidth: '45px', maxHeight: '45px', objectFit: 'contain' }} />
                ) : (
                  <i className="bi bi-image" style={{ color: '#ccc' }}></i>
                )}
              </div>
              <div>
                <div style={{ fontWeight: '600', color: '#333' }}>{selectedItemForAddon.name}</div>
                <div style={{ fontSize: '14px', color: primaryColor, fontWeight: '600' }}>{formatCurrency(selectedItemForAddon.price)}</div>
              </div>
            </div>
          )}

          <div style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '10px' }}>Available Addons:</div>

          {addonsLoading ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
              <Spinner animation="border" size="sm" style={{ color: primaryColor }} />
              <p style={{ margin: '10px 0 0', fontSize: '13px' }}>Loading addons...</p>
            </div>
          ) : !selectedItemForAddon?.addonsId ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              <i className="bi bi-x-circle" style={{ fontSize: '2rem', display: 'block', marginBottom: '10px' }}></i>
              <p style={{ margin: 0 }}>No addons for this item</p>
            </div>
          ) : itemAddons.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              <i className="bi bi-inbox" style={{ fontSize: '2rem', display: 'block', marginBottom: '10px' }}></i>
              <p style={{ margin: 0 }}>No addons available</p>
            </div>
          ) : (
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {itemAddons.map(addon => {
                const selectedAddon = selectedAddons.find(a => a.id === addon.id);
                const isSelected = !!selectedAddon;
                return (
                  <div
                    key={addon.id}
                    style={{
                      padding: '10px 12px',
                      marginBottom: '8px',
                      borderRadius: '8px',
                      border: isSelected ? `2px solid ${primaryColor}` : '1px solid #eee',
                      background: isSelected ? '#fff5f5' : '#fff',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div
                      onClick={() => toggleAddon(addon)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          border: isSelected ? `2px solid ${primaryColor}` : '2px solid #ddd',
                          background: isSelected ? primaryColor : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {isSelected && <i className="bi bi-check" style={{ color: '#fff', fontSize: '12px' }}></i>}
                        </div>
                        <span style={{ fontWeight: '500', color: '#333' }}>{addon.name}</span>
                      </div>
                      <span style={{ fontWeight: '600', color: primaryColor }}>+{formatCurrency(addon.price)}</span>
                    </div>
                    {/* Quantity controls for selected addon */}
                    {isSelected && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '10px',
                        paddingTop: '10px',
                        borderTop: '1px dashed #eee'
                      }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>Quantity:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            onClick={(e) => updateAddonQuantity(addon.id, -1, e)}
                            style={{
                              width: '28px',
                              height: '28px',
                              border: '1px solid #ddd',
                              background: '#fff',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '700',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >−</button>
                          <span style={{ fontWeight: '700', minWidth: '24px', textAlign: 'center', fontSize: '14px' }}>
                            {selectedAddon.quantity || 1}
                          </span>
                          <button
                            onClick={(e) => updateAddonQuantity(addon.id, 1, e)}
                            style={{
                              width: '28px',
                              height: '28px',
                              border: '1px solid #ddd',
                              background: '#fff',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: '700',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >+</button>
                          <span style={{ fontSize: '13px', color: primaryColor, fontWeight: '600', marginLeft: '8px' }}>
                            = {formatCurrency(addon.price * (selectedAddon.quantity || 1))}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Total with addons */}
          {selectedAddons.length > 0 && selectedItemForAddon && (
            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', marginTop: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '13px', color: '#666' }}>
                <span>Item Price:</span>
                <span>{formatCurrency(selectedItemForAddon.price)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '13px', color: '#666' }}>
                <span>Addons ({selectedAddons.reduce((sum, a) => sum + (a.quantity || 1), 0)}):</span>
                <span>+{formatCurrency(selectedAddons.reduce((sum, a) => sum + (a.price * (a.quantity || 1)), 0))}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px dashed #ddd', fontWeight: '600' }}>
                <span>Total:</span>
                <span style={{ color: primaryColor }}>{formatCurrency(selectedItemForAddon.price + selectedAddons.reduce((sum, a) => sum + (a.price * (a.quantity || 1)), 0))}</span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #eee', padding: '15px 20px' }}>
          <Button variant="light" onClick={() => setShowAddonModal(false)} style={{ borderRadius: '8px', fontWeight: '500' }}>
            Cancel
          </Button>
          <Button
            onClick={addItemWithAddons}
            style={{ background: primaryColor, border: 'none', borderRadius: '8px', fontWeight: '500', padding: '8px 20px' }}
          >
            <i className="bi bi-cart-plus me-2"></i>
            Add to Cart
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default POS;
